import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogOverlay, Dropdown } from '@shopflo/ui'
import { Check, X } from 'react-feather'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { useSupportPageContext } from 'src/lib/contexts/SupportPageContext'
import dynamic from 'next/dynamic'
import { BackendPriority, FeedbackReponseType } from 'src/lib/types/support'
import {
  updateTicketPayload,
  createTicketPayload,
  getMediaLinks,
  validateFeatureSuggestionForm
} from 'src/lib/utils/support'
import { useMemo, useRef, useState, useCallback } from 'react'
import { deleteRequest, patchRequest, postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import TextFieldError from 'src/components/common/form-elements/TextFieldError'
import FilePreviewGallery from 'src/components/common/form-elements/MultiFileUploadPreview'
import { useUser } from 'src/lib/contexts/UserContext'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { ADFEditor } from './ADFEditor'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { classNames } from 'src/lib/utils/helpers'

const ADFEditorSkeleton = () => {
  return <div className='w-full bg-gray-200 h-20 animate-pulse rounded'></div>
}

const FeatureSuggestionDialog = () => {
  const {
    state: { isFeatureDialogOpen, featureSuggestionForm },
    actions: { handleFeatureSuggestionDialog, handleFeatureSuggestion }
  } = useSupportPageContext()

  const { user } = useUser()
  const { state: organisationData } = useOrganisationContext()
  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()

  const mediaFiles = useMemo(() => getMediaLinks(featureSuggestionForm.attachment), [featureSuggestionForm.attachment])

  const [errors, setErrors] = useState<Record<string, string>>({})
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})
  const isEditing = Boolean(featureSuggestionForm.issueId)
  const [mediaToDelete, setMediaToDelete] = useState<number[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const assignRef = useCallback(
    (name: string) => (node: HTMLElement | null) => {
      if (node) fieldRefs.current[name] = node
    },
    []
  )

  const scrollToFirstError = () => {
    for (const key of Object.keys(errors)) {
      const el = fieldRefs.current[key]
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        break
      }
    }
  }

  const handleDeleteMedia = (form: FeedbackReponseType) => {
    if (!isEditing || mediaToDelete.length === 0) return []

    return mediaToDelete.map(media =>
      deleteRequest(`/feedback/${form.issueId}/delete-attachment/${media}`, apiURI.SETTLEMENT_ENGINE)
    )
  }

  const handleSave = async () => {
    if (isSaving) return

    const { hasError, errors: validationErrors } = validateFeatureSuggestionForm(featureSuggestionForm)
    setErrors(validationErrors)
    if (hasError) {
      scrollToFirstError()
      return
    }

    setIsSaving(true)

    const constructName = () => {
      if (user?.firstName && user.lastName) {
        return user.firstName + ' ' + user?.lastName
      }
      return user?.firstName ?? ''
    }

    const formWithUserDetails: FeedbackReponseType = {
      ...featureSuggestionForm,
      userDetails: {
        name: constructName(),
        phone_number: user?.phoneNumber ?? '',
        company_role: user?.role ?? '',
        brand_category: organisationData.taxDetails.category,
        created_by: user?.email ?? ''
      }
    }

    setLoaderProperties({ isLoaderVisible: true })
    try {
      if (isEditing && mediaToDelete.length) {
        const deletePromises = handleDeleteMedia(formWithUserDetails)
        const resp = await Promise.all(deletePromises)
        setMediaToDelete([])
      }

      const payload = isEditing ? updateTicketPayload(formWithUserDetails) : createTicketPayload(formWithUserDetails)

      const url = isEditing ? `/feedback/${formWithUserDetails.issueId}` : `/feedback`
      const method = isEditing ? patchRequest : postRequest
      const response = await method(url, apiURI.SETTLEMENT_ENGINE, payload)

      if (!response.data) {
        throw new Error('Failed to save your suggestion')
      }
      successToast(t('feedback_submit_success'))
      handleFeatureSuggestionDialog(false)
    } catch (err) {
      errorToast('Something went wrong. Please try again later.')
      console.error(err)
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={isFeatureDialogOpen} onOpenChange={() => handleFeatureSuggestionDialog(false)}>
      <DialogOverlay className='fixed inset-0 bg-black/50 z-40' />
      <DialogContent className='max-w-[80%] sm:max-w-[500px] md:max-w-[700px] lg:max-w-[900px] bg-white text-black z-50 fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-2xl'>
        <DialogHeader>
          <DialogTitle>
            <div className='flex justify-between items-center'>
              <p>{t('support.suggest_new_feature')}</p>
              <X onClick={() => handleFeatureSuggestionDialog(false)} className='cursor-pointer' />
            </div>
          </DialogTitle>
        </DialogHeader>

        <form className='grid gap-4 max-h-[500px] overflow-auto pr-4'>
          <div ref={assignRef('summary')}>
            <InputTextField
              label={t('support.feature_name')}
              handleChange={e => handleFeatureSuggestion({ ...featureSuggestionForm, summary: e.target.value })}
              textFieldId='feature-name'
              isRequired={true}
              inputValue={featureSuggestionForm.summary}
              labelCustomClass={'!text-coal-dark !text-sm !font-medium'}
              showCharacterCountConfig={{ maxLength: 100, showOnlyOnFocus: false, showCharacterCountOnLabel: true }}
              errorStatus={Boolean(errors.summary)}
              customErrorMessage={''}
              errorMessage={errors.summary}
              toolTipRequired={false}
            />
          </div>

          <div ref={assignRef('description')} className='flex flex-col gap-2 relative z-[5] min-h-60'>
            <p className='text-sm font-medium text-coal-dark'>
              <span>{t('support.describe_problem_statement')}</span>
              <span className='text-red-500'>*</span>
            </p>
            <ADFEditor
              value={featureSuggestionForm.description}
              handleChange={adf => handleFeatureSuggestion({ ...featureSuggestionForm, description: adf })}
            />
            {errors.description && <TextFieldError message={errors.description} />}
          </div>

          <div ref={assignRef('impactArea')} className='flex flex-col gap-2 relative z-[4] min-h-60'>
            <p className='text-sm font-medium text-coal-dark'>
              <span>{t('support.impact_area')}</span>
              <span className='text-red-500'>*</span>
            </p>
            <ADFEditor
              value={featureSuggestionForm.impactArea}
              handleChange={adf => handleFeatureSuggestion({ ...featureSuggestionForm, impactArea: adf })}
            />
            {errors.impactArea && <TextFieldError message={errors.impactArea} />}
          </div>

          <div ref={assignRef('currentResolution')} className='flex flex-col gap-2 relative z-[3] min-h-60'>
            <p className='text-sm font-medium text-coal-dark'>
              <span>{t('support.manaing_issue')}</span>
              <span className='text-red-500'>*</span>
            </p>
            <ADFEditor
              value={featureSuggestionForm.currentResolution}
              handleChange={adf => handleFeatureSuggestion({ ...featureSuggestionForm, currentResolution: adf })}
            />
            {errors.currentResolution && <TextFieldError message={errors.currentResolution} />}
          </div>

          <div ref={assignRef('suggestedSolve')} className='flex flex-col gap-2 relative z-[2] min-h-60'>
            <p className='text-sm font-medium text-coal-dark'>
              <span>{t('support.proposed_solution')}</span>
              <span className='text-red-500'>*</span>
            </p>
            <ADFEditor
              value={featureSuggestionForm.suggestedSolve}
              handleChange={adf => handleFeatureSuggestion({ ...featureSuggestionForm, suggestedSolve: adf })}
            />
            {errors.suggestedSolve && <TextFieldError message={errors.suggestedSolve} />}
          </div>

          <div ref={assignRef('priority')} className='flex flex-col gap-2'>
            <p className='text-sm font-medium text-coal-dark'>
              <span>{t('support.priority')}</span>
              <span className='text-red-500'>*</span>
            </p>
            <Dropdown
              options={[
                { title: 'Critical/High', value: 'HIGH' },
                { title: 'Medium', value: 'MEDIUM' },
                { title: 'Low', value: 'LOW' }
              ]}
              onChange={(value: BackendPriority) =>
                handleFeatureSuggestion({ ...featureSuggestionForm, priority: value })
              }
              value={featureSuggestionForm.priority}
            />
            {errors.priority && <TextFieldError message={errors.priority} />}
          </div>

          <div className='flex gap-2 flex-col'>
            <p className='text-sm font-medium text-coal-dark'>{t('support.upload')}</p>
            <FilePreviewGallery
              initialUrls={mediaFiles}
              onFilesChange={(files: File[], id?: number) => {
                if (id !== undefined) {
                  setMediaToDelete(prev => [...prev, id])
                }
                handleFeatureSuggestion({ ...featureSuggestionForm, files })
              }}
              maxFileSizeMB={100}
              maxFiles={4}
            />
          </div>
        </form>

        <DialogFooter>
          <div className='flex justify-end gap-3'>
            <button
              onClick={() => handleFeatureSuggestionDialog(false)}
              className='px-3 py-2 bg-white text-coal-dark rounded-md border-1 border-coal-lighter cursor-pointer text-sm font-semibold'
            >
              {t('close')}
            </button>
            <button
              type='submit'
              className={classNames(
                `px-3 py-2 bg-carbon flex gap-2 items-center rounded-lg`,
                isSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              )}
              onClick={handleSave}
              disabled={isSaving}
            >
              <Check size={16} color='#FFFFFF' />
              <span className='text-sm font-semibold text-white'>{isEditing ? 'Save' : 'Submit'}</span>
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default FeatureSuggestionDialog
